import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import HighPerformanceAd from "../components/HighPerformanceAd";
import { Sidebar } from "../components/Sidebar";
import { Header } from "../components/Header";
import { Footer } from "../components/ Footer";
import iconBTC from  "../assets/images/bitcoin.png";
import iconETH from "../assets/images/eth.png";
import iconSOL from "../assets/images/solana.png";
import iconWAV from "../assets/images/Waves.png";

import axios from "../axiosInstance";

function Toast({ message, type = "error", onClose }) {
  return (
    <div className={`fixed top-6 left-1/2 transform -translate-x-1/2 z-50 px-4 py-3 rounded shadow-lg text-white ${type === "error" ? "bg-red-600" : "bg-green-600"}`}>
      <div className="flex items-center space-x-2">
        <span>{message}</span>
        <button onClick={onClose} className="ml-2 text-white hover:text-gray-300 font-bold">×</button>
      </div>
    </div>
  );
}

// Fees in percentage + min withdrawal in coinz
const FEE_CONFIG = {
  btc: { min: 1000000, percentage: 0.02 },
  eth: { min: 200000, percentage: 0.01 },
  sol: { min: 100000, percentage: 0.005 },
  waves: { min: 100000, percentage: 0.005 },
};

export default function Withdraw({ onLogout }) {
  const [options, setOptions] = useState(0);
  const [coinz, setCoinz] = useState(0);
  const [selfWeeklyProfit, setSelfWeeklyProfit] = useState(0);
  const [weeklyProfit, setWeeklyProfit] = useState(0);
  const [withdrawals, setWithdrawals] = useState({});
  const [withdrawHistory, setWithdrawHistory] = useState([]);
  const [toast, setToast] = useState({ message: '', type: '' });
  const [wallets, setWallets] = useState({ btc: '', eth: '', sol: '', waves: '' });

  const showToast = (message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: '', type: '' }), 3000);
  };

  const getUserData = () => {
    axios.get(`/api/user/me`, { withCredentials: true }).then((response) => {
      const { coinz, options, systemCoinz, weeklyProfit, wallets } = response.data;
      const valeurCalculee = options > 0 ? ((systemCoinz / weeklyProfit) * options) : 0;
      setSelfWeeklyProfit(valeurCalculee);
      setCoinz(coinz);
      setOptions(options);
      setWeeklyProfit(weeklyProfit);
      setWallets(wallets || {});
    }).catch((error) => {
      console.error("Error fetching user data:", error);
    });
  };

    // Fetch user's withdrawals
    const fetchWithdrawals = async () => {
      try {
        const response = await axios.get(`/api/withdraw`, { withCredentials: true });
        setWithdrawHistory(response.data); // ✅ on remplit bien l'historique
      } catch (error) {
        showToast('Error fetching withdrawals.');
      }
    };
    
  const handleSingleWithdraw = async (symbol) => {
    const amount = parseInt(withdrawals[symbol]?.amount || 0, 10);
    const address = wallets[symbol];
    const config = FEE_CONFIG[symbol];

    if (amount < config.min || !address) {
      showToast("Invalid amount or wallet address", "error");
      return;
    }

    const fee = Math.floor(amount * config.percentage);
    const netAmount = amount - fee;

    try {
      const response = await axios.post(
        `/api/withdraw`,
        { symbol, amount, fee },
        { withCredentials: true }
      );
      setWithdrawals(response.data.form || {});
      fetchWithdrawals();
      showToast(response.data.message, "success");
      getUserData();
    } catch (error) {
      showToast(error.response?.data?.error || "Error during withdrawal", "error");
    }
  };

  useEffect(() => {
    document.title = "Withdraw | CoinzUp";
    
    getUserData();

    fetchWithdrawals(); // ✅ maintenant ça charge les retraits aussi
  }, []);

  const getIconPath = (symbol) => {
    const map = {
      btc: iconBTC,
      eth: iconETH,
      sol: iconSOL,
      waves: iconWAV, 
    };
    return map[symbol];
  };

  return (
    <>
      {toast.message && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: '' })} />
      )}
      <div className="min-h-screen flex flex-col bg-gray-900 text-white font-sans">
        <div className="flex flex-1 min-h-[calc(100vh-64px)]">
          <Sidebar onLogout={onLogout} />
          <main className="flex-1 px-4 sm:px-10 py-8 overflow-auto min-w-0 space-y-10">
            <Header title="Withdraw" balance={coinz} optionsOwned={options} estimatedWeeklyGain={selfWeeklyProfit} />
            <HighPerformanceAd keyId="fd5da3543c594f07b73630f461f10613" width={728} height={90} />
            <div className="max-w-5xl w-full mx-auto px-4 sm:px-6 lg:px-8 space-y-6">

            <Card className="bg-gray-800 border border-gray-700 shadow-sm">
              <CardContent className="p-5 text-sm text-gray-300 space-y-3">
                <h3 className="text-base font-semibold text-white">💡 Important Withdrawal Information</h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-400">
                  <li>
                    <span className="text-gray-300 font-medium">Minimum withdrawal amounts:</span>
                    <ul className="ml-5 space-y-1">
                      <li>• BTC: 1,000,000 coinz</li>
                      <li>• ETH: 200,000 coinz</li>
                      <li>• SOL: 100,000 coinz</li>
                      <li>• WAVES: 100,000 coinz</li>
                    </ul>
                  </li>
                  <li>
                    <span className="text-gray-300 font-medium">Fees:</span> 
                    BTC (2%), ETH (1%), SOL & WAVES (0.5%). Fees are deducted from the amount you enter.
                  </li>
                  <li>
                    <span className="text-gray-300 font-medium">Processing time:</span> 
                    Withdrawals are manually processed within <span className="text-white font-semibold">48 hours maximum</span>.
                  </li>
                </ul>
              </CardContent>
            </Card>

              <Card className="bg-gray-800 border border-gray-700 shadow-sm max-w-screen-lg mx-auto w-full">             

                <CardContent className="p-6 space-y-4">
                  <h2 className="text-xl font-bold">Withdraw Your Coinz</h2>
                  {["btc", "eth", "sol", "waves"].map((symbol) => {
                    const inputAmount = Number(withdrawals[symbol]?.amount || 0);
                    const config = FEE_CONFIG[symbol];
                    const hasWallet = Boolean(wallets[symbol]);
                    const fee = inputAmount * config.percentage;
                    const netAmount = Math.max(0, inputAmount - fee);
                    const usdValue = netAmount * 0.00001;

                    return (
                      <div key={symbol} className="flex flex-col sm:flex-row items-center gap-3 p-4 rounded-md bg-gray-900 border border-gray-700">
                        <img src={getIconPath(symbol)} alt={symbol} className="w-6 h-6 mr-2" />
                        <div className="w-[120px] text-sm font-medium uppercase text-gray-300 text-center truncate">{symbol}</div>
                        <div className="flex items-center gap-2 w-full sm:w-48">
                          <input
                            type="number"
                            min="0"
                            value={withdrawals[symbol]?.amount || ''}
                            onChange={(e) =>
                              setWithdrawals((prev) => ({
                                ...prev,
                                [symbol]: {
                                  ...prev[symbol],
                                  amount: e.target.value,
                                },
                              }))
                            }
                            className="w-32 p-2 rounded bg-gray-700 text-white border border-gray-600 text-sm"
                            placeholder="Amount"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setWithdrawals((prev) => ({
                                ...prev,
                                [symbol]: {
                                  ...prev[symbol],
                                  amount: coinz,
                                },
                              }))
                            }
                            className="text-xs text-blue-400 hover:underline"
                          >
                            (max)
                          </button>
                        </div>
                        <div className="hidden sm:block text-xs text-gray-400 w-full truncate">
                          {hasWallet ? wallets[symbol] : <span className="text-red-500">No wallet</span>}
                        </div>
                        <div className="text-sm text-gray-300 w-full sm:w-48">
                          <div>Fee: <span className="text-yellow-400 font-medium">{fee.toFixed(0)} coinz</span></div>
                          <div>Net: <span className="text-green-400 font-semibold">{netAmount.toFixed(0)} coinz</span></div>
                          <div className="text-xs text-gray-400">≈ ${usdValue.toFixed(4)}</div>
                        </div>
                        <div className="w-full sm:w-auto">
                          <button
                            onClick={() => handleSingleWithdraw(symbol)}
                            disabled={!hasWallet || inputAmount < config.min}
                            className="text-sm px-3 py-1.5 border border-green-600 text-green-400 rounded hover:bg-green-700/10 transition disabled:opacity-50"
                          >
                            Withdraw
                          </button>
                          {inputAmount > 0 && inputAmount < config.min && (
                            <div className="text-xs text-red-400 mt-1">
                              Minimum to withdraw: {config.min} coinz
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
              {/* History table unchanged */}
              <Card className="bg-gray-800 border border-gray-700 shadow-sm">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-4">Withdrawal History</h3>
                  <div className="w-full overflow-x-auto">
                    <table className="min-w-full text-sm text-left text-gray-300">
                      <thead>
                        <tr className="border-b border-gray-700">
                          <th className="px-4 py-2">ID</th>
                          <th className="px-4 py-2">Coinz</th>
                          <th className="px-4 py-2">Fee</th>
                          <th className="px-4 py-2">Address</th>
                          <th className="px-4 py-2">Status</th>
                          <th className="px-4 py-2">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {withdrawHistory.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="text-center py-4 text-gray-500">No withdrawals made.</td>
                          </tr>
                        ) : (
                          withdrawHistory.map((w) => (
                            <tr key={w.id} className="border-b border-gray-800 hover:bg-gray-700/30">
                              <td className="px-4 py-2">{w.id}</td>
                              <td className="px-4 py-2">{w.coinz_sent}</td>
                              <td className="px-4 py-2">{w.coinz_commission}</td>
                              <td className="px-4 py-2 break-all">{w.address}</td>
                              <td className="px-4 py-2 capitalize">{w.status}</td>
                              <td className="px-4 py-2">{w.requested_at ? new Date(w.requested_at).toLocaleString() : "N/A"}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
              <div className="mt-8">
                <HighPerformanceAd keyId="96e99453a24be0353f2fe26dc0b9b728" width={468} height={60} />
              </div>
            </div>
          </main>
        </div>
        <Footer />
      </div>
    </>
  );
}

